/**
 * Compares an ISO 8601 datetime string with a filter date for AG Grid date filtering.
 *
 * @param filterLocalDateAtMidnight - The filter date (at midnight local time) from AG Grid
 * @param cellValue - The cell value as an ISO 8601 datetime string (e.g., '2025-10-02T11:00:00-07:00')
 * @param considerTime - Whether to include time in comparison (default: false, only compare dates)
 * @returns -1 if cellValue is before filter date, 0 if equal (same day), 1 if after.
 *          Also returns -1 if cellValue is null, empty, or an invalid date string.
 */
export const isoDateComparator = (
  filterLocalDateAtMidnight: Date,
  cellValue: string,
  considerTime: boolean = false
): number => {
  if (cellValue == null || cellValue === '') {
    return -1; // Treat null/empty as "less than" any date
  }

  // Parse ISO 8601 datetime string
  const cellDate = new Date(cellValue);

  // Check if the date is invalid
  if (isNaN(cellDate.getTime())) {
    return -1; // Treat invalid dates as "less than" any date
  }

  if (considerTime) {
    // Compare full datetime including time component
    // Both dates are already in local timezone after parsing
    if (cellDate < filterLocalDateAtMidnight) {
      return -1;
    } else if (cellDate > filterLocalDateAtMidnight) {
      return 1;
    } else {
      return 0;
    }
  } else {
    // Compare dates only (ignore time component)
    // For date-only comparison, extract calendar date from ISO string directly
    // to avoid timezone conversion issues
    const dateMatch = cellValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!dateMatch) {
      return -1; // Invalid format
    }

    const cellYear = parseInt(dateMatch[1], 10);
    const cellMonth = parseInt(dateMatch[2], 10) - 1; // JS months are 0-indexed
    const cellDay = parseInt(dateMatch[3], 10);

    const filterYear = filterLocalDateAtMidnight.getFullYear();
    const filterMonth = filterLocalDateAtMidnight.getMonth();
    const filterDay = filterLocalDateAtMidnight.getDate();

    // Compare year, month, and day
    if (cellYear !== filterYear) {
      return cellYear < filterYear ? -1 : 1;
    }
    if (cellMonth !== filterMonth) {
      return cellMonth < filterMonth ? -1 : 1;
    }
    if (cellDay !== filterDay) {
      return cellDay < filterDay ? -1 : 1;
    }
    return 0;
  }
}; /**
 * Date-only comparator wrapper that ignores time component.
 * Use this for date fields where time should not affect filtering.
 */
export const isoDateOnlyComparator = (
  filterLocalDateAtMidnight: Date,
  cellValue: string
): number => {
  return isoDateComparator(filterLocalDateAtMidnight, cellValue, false);
};

/**
 * DateTime comparator wrapper that includes time component.
 * Use this for datetime fields where time is significant.
 */
export const isoDateTimeComparator = (
  filterLocalDateAtMidnight: Date,
  cellValue: string
): number => {
  return isoDateComparator(filterLocalDateAtMidnight, cellValue, true);
};
